// src/ErrorBoundary.jsx
import React from "react";

/**
 * Catches render errors from a subtree and drops that subtree instead of
 * letting the whole page go blank.
 *
 * Used around the optional furniture — the weather widget in particular reads
 * data.weather[0] and data.main.temp from a third-party response, so a payload
 * shaped differently than expected throws during render. Without a boundary
 * that takes the entire site white, which is a poor trade for a decorative
 * strip at the foot of the page.
 *
 * Renders `fallback` (null by default) when something throws. Errors still
 * reach the console, and GA if gtag is present, so failures stay visible
 * rather than being silently swallowed.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    const label = this.props.label || "unknown";
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${label}]`, error, info?.componentStack);
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "exception", {
        description: `${label}: ${error?.message || error}`,
        fatal: false,
      });
    }
  }

  render() {
    if (this.state.failed) return this.props.fallback ?? null;
    return this.props.children;
  }
}
